import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  CheckCircle,
  Crown,
  Zap,
  BookOpen,
  Terminal,
  Award,
  Users,
  Loader2,
  Tag,
  Sparkles,
} from "lucide-react";

const Pricing = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  // Promo code
  const [promoCode, setPromoCode] = useState("");
  const [validatedPromo, setValidatedPromo] = useState(null); // {discount_percent, name, code}
  const [validatingPromo, setValidatingPromo] = useState(false);

  // General active promo
  const [generalPromo, setGeneralPromo] = useState(null); // {active, discount_percent, name, ...}

  useEffect(() => {
    fetchGeneralPromo();
  }, []);

  const fetchGeneralPromo = async () => {
    try {
      const res = await axios.get(`${API}/promotions/active`);
      if (res.data.active) {
        setGeneralPromo(res.data);
      }
    } catch {
      // no promo
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    if (!user) {
      toast.error("Connectez-vous pour utiliser un code promo");
      return;
    }
    setValidatingPromo(true);
    try {
      const res = await axios.post(`${API}/promotions/validate`,
        { code: promoCode, item_type: "subscriptions" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setValidatedPromo(res.data);
      toast.success(`Code promo valide ! -${res.data.discount_percent}%`);
    } catch (error) {
      setValidatedPromo(null);
      toast.error(error.response?.data?.detail || "Code promo invalide");
    } finally {
      setValidatingPromo(false);
    }
  };

  // Calculate best discount for display
  const getBestDiscount = () => {
    let best = 0;
    if (generalPromo?.active) best = generalPromo.discount_percent;
    if (validatedPromo?.discount_percent > best) best = validatedPromo.discount_percent;
    return best;
  };

  const discountPercent = getBestDiscount();

  const plans = [
    {
      name: "Gratuit",
      price: 0,
      period: "",
      description: "Pour découvrir la plateforme",
      features: [
        "Accès aux leçons gratuites",
        "Terminal interactif limité",
        "Communauté Discord",
      ],
      notIncluded: [
        "Cours complets",
        "Labs illimités",
        "Certificats",
        "Support prioritaire",
      ],
      cta: "Commencer",
      popular: false,
    },
    {
      name: "Mensuel",
      price: 29.99,
      period: "/mois",
      planId: "monthly",
      description: "Accès complet, flexible",
      features: [
        "Tous les cours",
        "Labs interactifs illimités",
        "Certificats de complétion",
        "Assistant IA",
        "Support email",
        "Annulation à tout moment",
      ],
      notIncluded: [],
      cta: "S'abonner",
      popular: true,
    },
    {
      name: "Annuel",
      price: 199.99,
      period: "/an",
      planId: "yearly",
      description: "Économisez 45%",
      features: [
        "Tout le plan Mensuel",
        "2 mois gratuits",
        "Support prioritaire",
        "Accès anticipé aux nouveaux cours",
        "Sessions de mentorat (2/an)",
      ],
      notIncluded: [],
      cta: "S'abonner",
      popular: false,
      savings: "Économisez $159.89",
    },
  ];

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate("/auth?register=true", { state: { from: "/pricing" } });
      return;
    }

    if (!planId) {
      navigate("/dashboard");
      return;
    }

    setLoading(planId);

    try {
      const params = new URLSearchParams();
      params.append("plan", planId);
      if (validatedPromo?.code) params.append("promo_code", validatedPromo.code);
      else if (promoCode.trim()) params.append("promo_code", promoCode.trim());

      const response = await axios.post(
        `${API}/payments/checkout/subscription?${params.toString()}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            origin: window.location.origin,
          },
        }
      );

      window.location.href = response.data.url;
    } catch (error) {
      console.error("Subscription error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'abonnement");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <Navbar />

      {/* General Promo Banner */}
      {generalPromo?.active && (
        <div className="bg-gradient-to-r from-primary/20 via-purple-500/20 to-primary/20 border-b border-primary/30">
          <div className="max-w-7xl mx-auto px-4 py-3 text-center flex items-center justify-center gap-3">
            <Sparkles className="w-5 h-5 text-primary animate-pulse" />
            <span className="font-bold text-primary">
              🔥 {generalPromo.name} — -{generalPromo.discount_percent}% sur {generalPromo.applies_to === "all" ? "tout le site" : generalPromo.applies_to === "courses" ? "tous les cours" : "tous les abonnements"} !
            </span>
            {generalPromo.end_date && (
              <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">
                Jusqu'au {new Date(generalPromo.end_date).toLocaleDateString("fr-FR")}
              </Badge>
            )}
            <Sparkles className="w-5 h-5 text-primary animate-pulse" />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="relative bg-card/30 border-b border-border/40 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">
            <Crown className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Tarifs simples</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            Choisissez votre plan
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Accédez à tous nos cours, labs interactifs et certifications. 
            Annulez à tout moment.
          </p>

          {/* Promo Code Input */}
          <div className="max-w-sm mx-auto flex gap-2">
            <div className="relative flex-1">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Code promo"
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); if (validatedPromo) setValidatedPromo(null); }}
                className="pl-10 font-mono uppercase"
                onKeyDown={(e) => { if (e.key === "Enter") validatePromoCode(); }}
              />
            </div>
            <Button variant="outline" onClick={validatePromoCode} disabled={validatingPromo || !promoCode.trim()}>
              {validatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer"}
            </Button>
          </div>
          {validatedPromo && (
            <p className="text-sm text-primary mt-2 font-medium">
              ✅ Code "{validatedPromo.code}" appliqué : -{validatedPromo.discount_percent}%
            </p>
          )}
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-card border rounded-lg overflow-hidden ${
                plan.popular
                  ? "border-primary shadow-lg shadow-primary/10"
                  : "border-border"
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-center py-1 text-sm font-medium">
                  Le plus populaire
                </div>
              )}

              <div className={`p-6 ${plan.popular ? "pt-10" : ""}`}>
                <h3 className="text-xl font-heading font-bold mb-2">{plan.name}</h3>
                <p className="text-muted-foreground text-sm mb-4">{plan.description}</p>

                <div className="mb-6">
                  {plan.price > 0 && discountPercent > 0 ? (
                    <>
                      <span className="text-lg text-muted-foreground line-through mr-2">${plan.price}</span>
                      <span className="text-4xl font-heading font-bold text-primary">
                        ${(plan.price * (1 - discountPercent / 100)).toFixed(2)}
                      </span>
                      <span className="text-muted-foreground">{plan.period}</span>
                      <div className="mt-1">
                        <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">
                          -{discountPercent}%
                        </Badge>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-heading font-bold">${plan.price}</span>
                      <span className="text-muted-foreground">{plan.period}</span>
                    </>
                  )}
                </div>

                {plan.savings && (
                  <div className="mb-4 inline-block bg-primary/20 text-primary text-sm font-medium px-3 py-1 rounded-full">
                    {plan.savings}
                  </div>
                )}

                <Button
                  onClick={() => handleSubscribe(plan.planId)}
                  disabled={loading === plan.planId}
                  className={`w-full ${plan.popular ? "glow-green" : ""}`}
                  variant={plan.popular ? "default" : "outline"}
                  data-testid={`plan-${plan.planId || "free"}-btn`}
                >
                  {loading === plan.planId ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    plan.cta
                  )}
                </Button>
              </div>

              <div className="border-t border-border p-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  Inclus
                </h4>
                <ul className="space-y-3">
                  {plan.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.notIncluded.length > 0 && (
                  <>
                    <h4 className="font-medium mt-6 mb-4 text-muted-foreground">
                      Non inclus
                    </h4>
                    <ul className="space-y-3">
                      {plan.notIncluded.map((feature, fIdx) => (
                        <li key={fIdx} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="w-4 h-4 mt-0.5 shrink-0 text-center">—</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="mt-20">
          <h2 className="text-2xl font-heading font-bold text-center mb-12">
            Tout ce dont vous avez besoin pour réussir
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <BookOpen className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="font-heading font-bold mb-2">50+ Cours</h3>
              <p className="text-sm text-muted-foreground">
                Docker, Kubernetes, AWS, Terraform et plus
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Terminal className="w-10 h-10 text-secondary mx-auto mb-4" />
              <h3 className="font-heading font-bold mb-2">Labs Pratiques</h3>
              <p className="text-sm text-muted-foreground">
                Environnements Cloud interactifs
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Award className="w-10 h-10 text-accent mx-auto mb-4" />
              <h3 className="font-heading font-bold mb-2">Certificats</h3>
              <p className="text-sm text-muted-foreground">
                Prouvez vos compétences
              </p>
            </div>
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Users className="w-10 h-10 text-purple-400 mx-auto mb-4" />
              <h3 className="font-heading font-bold mb-2">Communauté</h3>
              <p className="text-sm text-muted-foreground">
                Échangez avec +10K devs
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Pricing;
